import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, IndianRupee, Loader2, ShieldCheck } from "lucide-react";
import { load as loadCashfree } from "@cashfreepayments/cashfree-js";
import api, { formatApiErrorDetail, inr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useAuth } from "@/contexts/AuthContext";
import PayoutProtectionNotice from "@/components/locanest/PayoutProtectionNotice";

// Cache the Cashfree SDK instance so it's loaded once per session.
let _cfSdkPromise = null;
export function ensureCashfree(mode) {
  if (!_cfSdkPromise) _cfSdkPromise = loadCashfree({ mode: mode === "production" ? "production" : "sandbox" });
  return _cfSdkPromise;
}

// Backwards-compat stub — Razorpay SDK is no longer used. Left here so hot-reload
// doesn't crash callers during migration. Now returns a promise that resolves so
// the "sdk ready" flag stays true.
function ensureRazorpay() { return Promise.resolve(true); }

export default function BookingButton({ property, onBooked, paymentPaused = false, className = "" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [ci, setCi] = useState("");
  const [co, setCo] = useState("");
  const [blocked, setBlocked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sdkReady, setSdkReady] = useState(paymentPaused); // when paused we don't need Cashfree
  const [cfEnv, setCfEnv] = useState("sandbox");

  const isShort = property?.category === "short_stay";
  const rent = Number(property?.rent || 0);
  const ppn = Number(property?.category_fields?.price_per_night || 0);

  // Prewarm Cashfree SDK — skipped entirely in free-launch (paymentPaused) mode.
  useEffect(() => {
    if (paymentPaused) { setSdkReady(true); return; }
    api.get("/config").then(({ data }) => {
      const mode = data?.cashfree_env === "production" ? "production" : "sandbox";
      setCfEnv(mode);
      ensureCashfree(mode).then(() => setSdkReady(true)).catch(() => setSdkReady(false));
    }).catch(() => {
      ensureCashfree("sandbox").then(() => setSdkReady(true)).catch(() => setSdkReady(false));
    });
  }, [paymentPaused]);

  useEffect(() => {
    if (!open) return;
    if (isShort) api.get(`/properties/${property.id}/booked-dates`).then(({ data }) => setBlocked(data)).catch(() => {});
  }, [open, isShort, property?.id]);

  const nights = (ci && co) ? Math.max(0, Math.round((new Date(co) - new Date(ci)) / 86400000)) : 0;
  // Rental advance: 5% of rent, clamped to [ADVANCE_MIN_INR, ADVANCE_MAX_INR] (mirrors backend).
  // Short-stay: first night's price is the advance/token — remaining balance settled at check-in.
  const rawAdvance = Math.round(rent * 0.05);
  const rentalAdvance = Math.max(300, Math.min(rawAdvance, 5000));
  const advanceRupees = isShort ? ppn : rentalAdvance;
  const remainingShortStay = isShort && nights > 1 ? ppn * (nights - 1) : 0;
  const isBlockedRange = (from, to) => blocked.some((b) => !(to <= b.from || from >= b.to));

  const start = async () => {
    if (!user) return navigate("/login");
    if (user.role !== "tenant") return toast.error("Only tenants can book. Please switch/register a tenant account.");
    if (isShort) {
      if (!ci || !co) return toast.error("Pick check-in and check-out dates");
      if (nights < 1) return toast.error("Check-out must be after check-in");
      if (isBlockedRange(ci, co)) return toast.error("Selected dates overlap with an existing booking");
    }

    setBusy(true);
    try {
      const { data } = await api.post("/bookings/create", { property_id: property.id, check_in: ci || null, check_out: co || null });
      const booking = data.booking;
      setOpen(false);

      // FREE-LAUNCH: no payment step. Owner has already been notified server-side.
      if (data.payment_required === false) {
        toast.success(data.message || "Interest sent! The owner will reach out shortly.");
        onBooked?.();
        navigate(`/bookings/${booking.id}`);
        return;
      }

      const cf = await ensureCashfree(cfEnv);
      const result = await cf.checkout({
        paymentSessionId: data.payment_session_id,
        redirectTarget: "_modal",
      });
      if (result?.error) {
        toast.error(result.error?.message || "Payment cancelled");
        return;
      }
      // Cashfree resolves after modal closes (success/paid path). Verify authoritatively:
      try {
        await api.post("/bookings/verify", { booking_id: booking.id, order_id: data.cashfree_order_id });
        toast.success("Payment confirmed! Your reservation is active.");
        onBooked?.();
        navigate(`/bookings/${booking.id}`);
      } catch (err) {
        // Verify may fail if payment still pending — user can retry from bookings page.
        toast.info("Payment received — we're confirming with Cashfree. Check your bookings shortly.");
        navigate(`/bookings/${booking.id}`);
      }
    } catch (err) {
      const msg = err?.response ? formatApiErrorDetail(err.response.data?.detail) : (err?.message || "Could not send interest");
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button onClick={() => (user ? setOpen(true) : navigate(`/login?next=${encodeURIComponent(`/property/${property?.id}`)}`))} className={className || "w-full mt-3 bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md"} data-testid={className ? "reserve-btn-mobile" : "reserve-btn"}>
        <IndianRupee size={15} className="mr-1.5" /> {paymentPaused ? (isShort ? "Send interest for stay" : "Send interest") : `Reserve & pay ${isShort ? "for stay" : "advance"}`}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{paymentPaused ? "Send interest" : "Reserve"} · {property?.title}</DialogTitle>
            <DialogDescription>
              {paymentPaused
                ? (isShort
                    ? "Pick your dates and send interest — no payment needed right now. The owner will get an instant email and reach out to arrange your stay."
                    : "Send a reservation request to the owner — no advance/token needed during our free launch. The owner will get an instant email and reach out.")
                : (isShort
                    ? "Pick your dates. Pay the first night as advance to reserve — remaining balance is settled with the owner at check-in. Owner reject → full refund; tenant cancel → non-refundable."
                    : "Pay 5% of monthly rent as advance/token to reserve (min ₹300, max ₹5,000). Owner reject → full refund; tenant cancel → non-refundable.")}
            </DialogDescription>
          </DialogHeader>

          {isShort && (
            <div className="space-y-3">
              {/* Trigger row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Check-in</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="w-full mt-1 h-10 px-3 rounded-md border border-[#1A1F2C]/15 bg-white text-left text-sm hover:border-[#C85A32]/50" data-testid="booking-checkin">
                        {ci ? new Date(ci).toDateString().slice(4) : <span className="text-[#5C6B7A]">Pick check-in</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <BookingCalendar
                        selected={{ from: ci ? new Date(ci) : undefined, to: co ? new Date(co) : undefined }}
                        onSelect={(range) => {
                          setCi(range?.from ? range.from.toISOString().slice(0, 10) : "");
                          setCo(range?.to ? range.to.toISOString().slice(0, 10) : "");
                        }}
                        blocked={blocked}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>Check-out</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="w-full mt-1 h-10 px-3 rounded-md border border-[#1A1F2C]/15 bg-white text-left text-sm hover:border-[#C85A32]/50" data-testid="booking-checkout">
                        {co ? new Date(co).toDateString().slice(4) : <span className="text-[#5C6B7A]">Pick check-out</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <BookingCalendar
                        selected={{ from: ci ? new Date(ci) : undefined, to: co ? new Date(co) : undefined }}
                        onSelect={(range) => {
                          setCi(range?.from ? range.from.toISOString().slice(0, 10) : "");
                          setCo(range?.to ? range.to.toISOString().slice(0, 10) : "");
                        }}
                        blocked={blocked}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              {blocked.length > 0 && (
                <div className="text-xs text-[#5C6B7A] bg-[#F0EBE1] rounded p-2 flex items-center gap-2 flex-wrap">
                  <span className="inline-block w-3 h-3 rounded-sm bg-[#1A1F2C]/25" /> Greyed dates are already booked.
                </div>
              )}
              {ci && co && nights > 0 && (
                <div className="text-sm bg-[#D4DEC6]/40 rounded p-2 flex items-center gap-2">
                  <CalendarDays size={14} /> {nights} night{nights > 1 ? "s" : ""} · advance {inr(advanceRupees)}{remainingShortStay > 0 ? ` · balance at check-in ${inr(remainingShortStay)}` : ""}
                </div>
              )}
            </div>
          )}

          {!isShort && !paymentPaused && (
            <div className="text-sm bg-[#F0EBE1] rounded p-4 space-y-1">
              <div className="flex justify-between"><span>Monthly rent</span><span>{inr(rent)}</span></div>
              <div className="flex justify-between"><span>Advance / token (5%, capped ₹300–₹5,000)</span><span className="text-[#C85A32] font-semibold">{inr(advanceRupees)}</span></div>
              <div className="text-xs text-[#5C6B7A] mt-2">Rest of the rent + deposit are settled directly with the owner at move-in.</div>
            </div>
          )}
          {!isShort && paymentPaused && (
            <div className="text-sm bg-[#D4DEC6]/30 rounded p-4 space-y-1">
              <div className="flex justify-between"><span>Monthly rent</span><span>{inr(rent)}</span></div>
              <div className="flex justify-between"><span>Advance / token</span><span className="text-[#2B3A67] font-semibold">Paused — free launch</span></div>
              <div className="text-xs text-[#5C6B7A] mt-2">No payment now. Complete the deal directly with the owner and finalise through LocaNest for platform protections.</div>
            </div>
          )}

          <div className="text-xs text-[#5C6B7A] flex items-center gap-2">
            <ShieldCheck size={13} />
            {paymentPaused ? "Free launch — no advance collected." : `Payment secured via Cashfree ${sdkReady ? "" : "· loading gateway…"}`}
          </div>
          <PayoutProtectionNotice compact />

          <DialogFooter>
            <Button onClick={start} disabled={busy || !sdkReady || (isShort && (!ci || !co))} className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="booking-pay-btn">
              {busy && <Loader2 size={14} className="mr-1 animate-spin" />}
              {paymentPaused ? "Send interest" : `Pay ${inr(advanceRupees || 0)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}


function BookingCalendar({ selected, onSelect, blocked }) {
  // Convert blocked ranges into an array of blocked date objects for react-day-picker.
  const disabled = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const ranges = (blocked || []).map((b) => {
      const from = new Date(b.from);
      const to = new Date(b.to);
      to.setDate(to.getDate() - 1); // check_out is exclusive
      return { from, to };
    });
    return [{ before: today }, ...ranges];
  }, [blocked]);

  return (
    <Calendar
      mode="range"
      numberOfMonths={2}
      selected={selected}
      onSelect={onSelect}
      disabled={disabled}
      modifiersClassNames={{
        disabled: "line-through text-[#9AA1A8]",
      }}
      data-testid="booking-calendar"
    />
  );
}
